/**
 * Reader for the `to_parse` query parameter behind the auto-parsing enquiry
 * flow (an inbound sales@ email is turned into a link back into the system,
 * with the raw email body hung off the end of the URL).
 *
 * That link is built outside this app and the body is not fully encoded, so
 * `URLSearchParams` is the wrong tool for the job twice over:
 *
 *   1. Every unencoded `&` in the body reads as the start of a new query
 *      parameter, so the enquiry text was silently truncated at the first
 *      ampersand ("Bed & breakfast", "R&A", "Mr & Mrs Smith") and everything
 *      after it was thrown away.
 *   2. It applies form-encoding rules, turning every `+` into a space. The
 *      bodies arriving here are percent-encoded at best, so a `+` is a real
 *      plus sign - usually the prefix on an international phone number.
 *
 * So we slice the value out of the raw query string ourselves, from the first
 * `to_parse=` through to the end, and percent-decode it without touching `+`.
 *
 * The hash is stitched back on for the same reason: an unencoded `#` in the
 * body ("booking ref #12345") starts the URL fragment, so everything after it
 * lands in `location.hash` rather than `location.search`. Nothing in this app
 * uses fragments, so anything there belongs to the enquiry text.
 *
 * All of this assumes `to_parse` is the last parameter on the URL, which is
 * how the sending application builds it. Don't append another parameter after
 * it - it would be read as part of the enquiry body.
 */

/**
 * Percent-decode a value that may contain stray, unescaped `%` characters
 * ("10% off", "50%+ full"). `decodeURIComponent` throws on those outright, so
 * fall back to decoding each valid escape sequence on its own and leaving
 * anything malformed exactly as it came in.
 */
const safeDecode = (value) => {
	try {
		return decodeURIComponent(value)
	} catch (error) {
		return value.replace(/(?:%[0-9a-f]{2})+/gi, (sequence) => {
			try {
				return decodeURIComponent(sequence)
			} catch (sequenceError) {
				return sequence
			}
		})
	}
}

/**
 * Pull the enquiry text to auto-parse out of the current URL. Returns an empty
 * string when the parameter isn't present.
 */
export const readTextToParse = (url = `${window.location.search}${window.location.hash}`) => {
	const query = url.startsWith("?") ? url.slice(1) : url

	// Match the parameter at the start of the query string or after any `&`
	const parameter = query.match(/(?:^|&)to_parse=/)
	if (!parameter) return ""

	// Everything from here to the end of the string is the enquiry body
	const raw = query.slice(parameter.index + parameter[0].length)
	if (!raw) return ""

	return safeDecode(raw)
}
